import React, { createContext, useContext, useEffect, useState } from 'react';
import { savePii, clearPii } from '@/lib/tracking';

const STORAGE_KEY = 'osgd_funnel_answers';
const LANG_KEY = 'osgd_lang';
const FunnelContext = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function loadLang() {
  try {
    return localStorage.getItem(LANG_KEY) === 'es' ? 'es' : 'en';
  } catch (e) {
    return 'en';
  }
}

export function FunnelProvider({ children }) {
  const [answers, setAnswers] = useState(loadInitial);
  const [lang, setLangState] = useState(loadLang);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
    } catch (e) {
      // ignore
    }
  }, [answers]);

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    // Mirror any PII keys into the tracking layer so every subsequent
    // Meta CAPI event (PageView / ViewContent / Contact) carries the
    // hashed match keys. Meta's Event Match Quality jumps significantly
    // when even one of email/phone/name is present.
    if (['email', 'phone', 'first_name', 'last_name', 'city', 'state', 'zip_code', 'zip'].includes(key)) {
      try { savePii({ [key === 'zip' ? 'zip_code' : key]: value }); } catch (e) { /* ignore */ }
    }
  };

  const resetAnswers = () => {
    setAnswers({});
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    // Also clear tracking-layer PII so a fresh visitor to the same
    // device isn't accidentally attributed with the previous lead's
    // email/phone on their first PageView.
    try { clearPii(); } catch (e) { /* ignore */ }
  };

  const setLang = (l) => {
    const next = l === 'es' ? 'es' : 'en';
    setLangState(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch (e) {
      // ignore
    }
  };

  return (
    <FunnelContext.Provider value={{ answers, setAnswer, resetAnswers, lang, setLang }}>
      {children}
    </FunnelContext.Provider>
  );
}

export function useFunnel() {
  const ctx = useContext(FunnelContext);
  if (!ctx) throw new Error('useFunnel must be used within FunnelProvider');
  return ctx;
}
